import { useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import type { DropdownItemBase } from '@/components/Autocomplete/autocomplete.types';
import { FormAutocompleteField } from '@/components/Form/FormAutocompleteField/FormAutocompleteField';
import {
	MultiPrimaryAutocompleteField,
	type MultiPrimaryItem
} from '@/components/Form/FormAutocompleteField/MultiPrimaryAutocompleteField';
import { useDcrFields } from '@/features/DataChangeRequest/_constants/useDcrFields';
import { useDcrContext } from '@/features/DataChangeRequest/_context/DcrContext';
import { LOCAL_SPECIALTY_MAX_ITEMS } from '@/features/DataChangeRequest/_schemas/localSpecialtyValidation';
import { localSpecialtyRowLabel } from '@/features/DataChangeRequest/_utils/localSpecialtyRowLabel';
import { DcrFormController } from '@/features/DataChangeRequest/DcrFormController/DcrFormController';
import { FormSection } from '@/features/DataChangeRequest/DcrModal/FormSection/FormSection';

import type { ProfessionalSectionValuesIn } from '../../../_schemas/HcpSchemas/ProfessionalSection.validation';

export const HcpProfessionalSection = () => {
	const { t } = useTranslation();
	const { hcpFields } = useDcrFields();
	const {
		control,
		watch,
		formState: { dirtyFields }
	} = useFormContext<ProfessionalSectionValuesIn>();
	const section = 'professional';

	const {
		picklists: {
			hcpTypeOptions,
			medicalDegreeOptions,
			/* specialtyOptions, */
			statusOptions,
			localHcpTypeOptions,
			specialtyLocalOptions
		}
	} = useDcrContext();

	const rawLocalSpecs = watch('allSpecLocal_VId');

	const localSpecialties = useMemo(() => {
		return (rawLocalSpecs?.map((val, index) => ({
			id: typeof val === 'object' ? val.id : val,
			name:
				specialtyLocalOptions?.find(
					o => o.id === (val.id ?? val) || o.name === (val.name ?? val)
				)?.name ??
				val.name ??
				val,
			isPrimary: index === 0,
			fieldName: `specialty${index + 1}_V`
		})) ?? []) as MultiPrimaryItem[];
	}, [specialtyLocalOptions, rawLocalSpecs]);

	return (
		<FormSection title={t('Professional')} id={section}>
			<DcrFormController
				name='hcpType'
				section={section}
				control={control}
				render={({ field, fieldState }) => (
					<FormAutocompleteField
						label={hcpFields.hcpType.label}
						section={section}
						id='hcpType'
						isRequired
						options={hcpTypeOptions ?? []}
						defaultValue={field.value}
						indicateError={!!fieldState.error}
						isDirty={fieldState.isDirty}
						handleOnDropdownItemSelect={item => {
							const option = hcpTypeOptions?.find(opt => opt.id === item?.id);
							field.onChange(option?.name ?? item?.id ?? '');
						}}
					/>
				)}
			/>

			<DcrFormController
				name='hcpTypeLocal_VId'
				section={section}
				control={control}
				render={({ field, fieldState }) => (
					<FormAutocompleteField
						label={hcpFields.hcpTypeLocal_VId.label}
						section={section}
						id='hcpTypeLocal_VId'
						options={localHcpTypeOptions ?? []}
						defaultValue={field.value}
						indicateError={!!fieldState.error}
						isDirty={fieldState.isDirty}
						handleOnDropdownItemSelect={item => {
							const option = localHcpTypeOptions?.find(
								opt => opt.id === item?.id
							);
							field.onChange(option?.name ?? item?.id ?? '');
						}}
					/>
				)}
			/>

			<MultiPrimaryAutocompleteField
				name='degrees'
				section={section}
				label={hcpFields.degrees.label}
				isAutocomplete
				disableNonInitialItems
				options={medicalDegreeOptions ?? []}
				defaultItems={watch('degrees')}
				canAddItems={false}
			/>

			{/* Note(Filip): As of 27.01.2026 - Jonas reqested this to be hidden for the time being */}
			{/* <MultiPrimaryAutocompleteField
				name='hcpSpecialties'
				label={t('Global Specialties')}
				isAutocomplete
				disableNonInitialItems
				options={specialtyOptions ?? []}
				defaultItems={watch('hcpSpecialties')}
				canAddItems={false}
				readOnly
			/> */}

			<MultiPrimaryAutocompleteField
				name='allSpecLocal_VId'
				section={section}
				label={hcpFields.allSpecLocal_VId.label}
				isAutocomplete
				options={specialtyLocalOptions ?? []}
				defaultItems={localSpecialties}
				hidePrimaryButton
				oneRequired
				canRemoveItems
				max={LOCAL_SPECIALTY_MAX_ITEMS}
				rowLabel={localSpecialtyRowLabel(t)}
			/>

			<DcrFormController
				name='statusCda_V'
				section={section}
				control={control}
				render={({ field, fieldState }) => (
					<FormAutocompleteField<DropdownItemBase>
						label={hcpFields.statusCda_V.label}
						id='statusCda_V'
						section={section}
						name={field.name}
						options={statusOptions ?? []}
						defaultValue={(field.value ?? '') as string}
						indicateError={!!fieldState.error}
						isDirty={dirtyFields.statusCda_V}
						handleOnDropdownItemSelect={item => {
							const option = statusOptions?.find(opt => opt.id === item?.id);
							field.onChange(option?.name ?? item?.id ?? '');
						}}
					/>
				)}
			/>
		</FormSection>
	);
};
